import { ReactElement, useCallback, useEffect, useState } from 'react'
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material'
import { CheckCircle2Icon, XCircleIcon } from 'lucide-react'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
// import Meta from '@/components/base/meta'
import Layout from '@/components/layout/layout'
import { useAppStore } from '@/contexts/store'
import { useLocaleStore } from '@/store/locale'
import { translateApiMessage } from '@/utils/api-messages'

import { verifyEmailMutation } from '@/api/auth/mutations'

type VerificationStatus = 'loading' | 'success' | 'error'

export default function EmailVerifyPage() {
  const router = useRouter()
  const { strings } = useAppStore()
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { token } = router.query
  const [status, setStatus] = useState<VerificationStatus>('loading')
  const [message, setMessage] = useState<string>('')

  const verifyEmail = useCallback(
    async (verificationToken: string) => {
      try {
        const response = await verifyEmailMutation('', { arg: { token: verificationToken } })
        setMessage(translateApiMessage(response.message, currentLocale))
        setStatus('success')
      } catch (error: any) {
        setMessage(
          error.message || strings?.email_verify?.error?.verification_failed_fallback || ''
        )
        setStatus('error')
      }
    },
    [strings, currentLocale]
  )

  useEffect(() => {
    if (router.isReady && token && typeof token === 'string') {
      verifyEmail(token)
    } else if (router.isReady && !token) {
      setStatus('error')
      setMessage(strings?.email_verify?.error?.missing_token || '')
    }
  }, [router.isReady, token, verifyEmail, strings])

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            borderRadius: 3,
            background: 'none'
          }}
        >
          {status === 'loading' && (
            <>
              <CircularProgress size={64} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                {strings?.email_verify?.loading?.title || ''}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {strings?.email_verify?.loading?.description || ''}
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <CheckCircle2Icon size={48} color="white" />
              </Box>
              <Typography variant="h5" gutterBottom color="success.main">
                {strings?.email_verify?.success?.title || ''}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {message}
              </Typography>
              <Button variant="contained" color="primary" href="/login">
                {strings?.email_verify?.success?.cta || ''}
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'error.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <XCircleIcon size={48} color="white" />
              </Box>
              <Typography variant="h5" gutterBottom color="error.main">
                {strings?.email_verify?.error?.title || ''}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {message}
              </Typography>
              <Button variant="contained" color="primary" href="/">
                {strings?.email_verify?.error?.cta || ''}
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  )
}

EmailVerifyPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout>
      {/* <Meta pageProps={pageProps} /> */}
      {page}
    </Layout>
  )
}

export async function getStaticProps() {
  return {
    props: {}
  }
}
